import { Link } from "react-router-dom";
import { InnoveraIcon } from "@libs/zapi-ui/assets";
import { FigmaTypography } from "@libs/zapi-ui/custom";
import { getRouteProjects, getRouteTermsOfUse } from "@/zapi-frontend/shared/const/router";

export function PrivacyNoticePage({ className = "" }: { className?: string }) {
  return (
    <article className={"mx-auto max-w-3xl px-4 pt-32 pb-10 text-gray-900 " + className}>
      <header
        className={
          "fixed top-0 left-0 w-full flex items-center border-b border-innoveraGrayscale-200 gap-4 px-[17px] py-0 h-[72px] font-medium bg-white"
        }
      >
        <Link
          data-testid="header-logo-link"
          to={getRouteProjects()}
          className="w-10 h-10 inline-flex justify-center items-center [&_svg>path]:hover:fill-innoveraOrange-400 [&_svg>path]:transition-[fill] [&_svg>path]:duration-[3ms]"
        >
          <InnoveraIcon className="w-6 h-6" />
        </Link>
        <FigmaTypography component="h1" variant="memo-heading-2">
          Privacy Notice
        </FigmaTypography>
      </header>

      <header className="mb-8 space-y-2">
        <p className="uppercase tracking-wide text-[12px] leading-[18px] font-medium text-gray-500">Confidential</p>
        <FigmaTypography component="h1" variant="heading-1">
          Privacy Notice
        </FigmaTypography>
        <p className="text-[14px] text-gray-600">
          Last Updated: <time dateTime="2026-01">January 2026</time>
        </p>
      </header>

      <section className="space-y-4">
        <p className="leading-relaxed">
          This Privacy Notice describes how Innovera.ai, Inc. ("<strong>Innovera</strong>", "<strong>we</strong>", "
          <strong>us</strong>", and/or "<strong>our</strong>") handles personal information that we collect from and
          about you when you visit our website (the "<strong>Site</strong>"); use our enterprise innovation management
          platform (the "<strong>Platform</strong>"), engage with us through our newsletters, emails, and branded social
          media accounts; or otherwise interact with us. Collectively, we describe our products and services - including
          our Site, Platform, and online publications and communications - as our "<strong>Services</strong>." This
          Privacy Notice also describes certain legal rights you may have, subject to applicable law, and how you can
          exercise them.
        </p>
        <p className="leading-relaxed">
          For the purposes of this Privacy Notice, "<strong>Personal Information</strong>" means information about an
          identified or identifiable person, and includes "personal data" as defined by the European Union's General
          Data Protection Regulation ("<strong>GDPR</strong>") or other applicable law. This includes information that
          alone or in combination with other information may be used to identify, contact, or locate you, such as your
          name, address, email address, or phone number.
        </p>
        <p className="leading-relaxed">
          Please read this notice carefully. In addition, please review our{" "}
          <Link to={getRouteTermsOfUse()} className="text-blue-500 underline">
            Terms of Use
          </Link>{" "}
          and any other agreement between you and us that governs your use of our Services.
        </p>
      </section>

      <section className="mt-8 space-y-4" id="information-we-collect">
        <FigmaTypography component="h2" variant="memo-heading-1">
          Information We Collect
        </FigmaTypography>

        <h3 className="text-lg font-semibold italic">Information you give us directly</h3>
        <p className="leading-relaxed">
          Some information we collect directly from you. For instance, we collect information that you voluntarily
          provide in the following ways:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <span className="font-medium">
              When you fill in forms on our Site, request a demo, or sign up to receive newsletters or other email
              communications from us.
            </span>{" "}
            When you fill in forms on our Site including when you request a demo or sign up for email updates from us,
            we may require that you provide us with certain information such as your first and last name, email address,
            phone number, social network information (such as a LinkedIn profile), current organization, position or
            title, and, if applicable, the name and affiliation of the person who referred you to us. We also collect
            any information you provide to us with as part of your request (such as how you heard about us or which
            offerings you would like to learn more about).
          </li>
          <li>
            <span className="font-medium">When you create an account on our Platform and populate your profile.</span>{" "}
            When you create an account on our Platform, we ask you provide your email address and a password we can use
            to authenticate you at sign in. We may also ask for a referral or access code which allows us to verify your
            affiliation with the employer who has provided you with access to our Platform. You have the option to add
            additional information, including a profile photo, your first and last name, your job title/occupation, a
            link to your LinkedIn profile, and your phone number.
          </li>
          <li>
            <span className="font-medium">When you use our Platform.</span> Because of the nature of our Platform, we
            may collect a wide range of information in connection with projects you are managing. Common types of
            Personal Information we may collect while providing our Services include files, tasks, project names, team
            names, calendar information, tech stack information, email messages and attachments, and other content. When
            we store, process, or transmit this type of information, we do so in our capacity as a "data processor" (or
            a comparable role such as a "service provider" in certain jurisdictions) and our use of that data is limited
            by our agreement with that customer. Innovera may develop, operate, and improve its technology using
            information that is not subject to customer contractual restrictions. Personal information processed on
            behalf of customers is governed by the applicable customer agreement.
          </li>
          <li>
            <span className="font-medium">When you register for an event or webinar.</span> If you register for an event
            or webinar we host or sponsor, we may collect your name, contact details, location, photos, demographic
            information, and any specific information relevant to the event for which you are registering.
          </li>
          <li>
            <span className="font-medium">
              When you email us to ask a question or request customer service or when you communicate with us via a
              third-party social media platform.
            </span>{" "}
            When you contact us by email; through our branded social media accounts; through any integrated chat
            providers we may provide; or by telephone, SMS, or physical mail, we may collect your name, username, email
            address, phone number, physical address, and the contents of your communication which can include any
            information you choose to provide.
          </li>
        </ul>

        <h3 className="text-lg font-semibold italic">Information we collect automatically when you use our Services</h3>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <span className="font-medium">Cookies/Web analytics.</span> We also collect certain information
            automatically as you navigate our Services or interact with our communications. To collect this information,
            we, or our third-party service providers may use cookies or similar technologies. A "cookie" is a small text
            file that websites send to a visitor's computer or other internet-connected device to uniquely identify the
            visitor's browser or to store information or settings in the browser. We, and our third-party service
            providers use cookies in order to do things such as recognize you when you return to our website, save your
            preferences (including your preferences regarding cookie handling), understand which sections of the website
            you find most interesting, ensure our Site is working properly, and prevent fraud. We use both session
            cookies (which expire when you close your browser) and persistent cookies (which stay on your computer until
            you delete them or until they expire).
            <p className="mt-2">The information we collect automatically can include the following:</p>
            <ul className="list-[circle] pl-6 space-y-1 mt-1">
              <li>
                <span className="font-medium">Information about the devices you use to access our Services</span>{" "}
                (including your internet protocol (IP) address, browser type, and operating system);
              </li>
              <li>
                <span className="font-medium">
                  The address of the web page visited before and after using our Services
                </span>
                ;
              </li>
              <li>
                <span className="font-medium">Details about your interactions with our Services</span> (such as the
                date, time, length of stay, and specific pages accessed during your visits);
              </li>
              <li>
                <span className="font-medium">Search terms used to reach our Services</span>;
              </li>
              <li>
                <span className="font-medium">Usage information</span> (such as the number and frequency of visitors to
                our Services); and
              </li>
              <li>
                <span className="font-medium">Location information</span> (such as general location information inferred
                from your IP address).
              </li>
            </ul>
            <p className="mt-2">
              If you do not want us to collect information using cookies, you can disable cookies, limit the types of
              cookies you allow, or set your browser to alert you when cookies are being sent. Please refer to the
              guidance offered by your chosen web browser if you would like help managing your cookie preferences.
              Please note that if you reject or block cookies, the Services may not work as intended.
            </p>
          </li>
          <li>
            <span className="font-medium">Email analytics.</span> We also use third-party services to help us send
            emails, manage subscribe/unsubscribe features, and improve the effectiveness of our email services. Emails
            contain a unique link to a transparent image which is loaded when the email is opened. We track the requests
            to download this hosted image. When you click on a hyperlink in the email, the unique redirect URL will
            reach our service for tracking then immediately forward you to the target destination. We use this
            information to better understand what information is of interest to our subscribers so we can produce more
            of that content for them. These third-party services do not use or sell this information.
          </li>
        </ul>

        <h3 className="text-lg font-semibold italic">Information we collect from other sources</h3>
        <p className="leading-relaxed">
          We may also obtain Personal Information from other sources, including the following:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <span className="font-medium">Another individual at your organization.</span> We may collect your Personal
            Information from your employer or another individual at your organization, if that person adds you as an
            "Authorized User" on their account or provides us with your email address to invite you to create an account
            on our Platform.
          </li>
          <li>
            <span className="font-medium">Other sources.</span> We may also obtain Personal Information from other
            sources, including our business partners, publicly available information on internet sites, and third
            parties that help us update, expand, and analyze our records and inform our networking and marketing
            efforts. For instance, we may use information from LinkedIn or from a third-party data provider to update
            information about you in our contact database. We also purchase data from third-party data providers to
            expand the depth and breadth of our Services, including updating and expanding company and investor profiles
            within our Platform.
          </li>
        </ul>
        <p className="leading-relaxed">
          We may combine the information we collect about you from these various sources. If a combination of
          information that we collect identifies you as an individual, we will treat the combined information as
          Personal Information.
        </p>
      </section>

      <section className="mt-8 space-y-4" id="how-we-use">
        <FigmaTypography component="h2" variant="memo-heading-1">
          How We Use Your Information
        </FigmaTypography>
        <p className="leading-relaxed">We use the information described above to:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <span className="font-medium">Provide our Services</span> (managing your access to and use of our Services
            and sending you technical notices, updates, security alerts, and support and administrative messages);
          </li>
          <li>
            <span className="font-medium">Operate and improve our Services</span> (including monitoring and analyzing
            usage, trends, and activities related to the Services and identifying, fixing, and troubleshooting bugs and
            service errors);
          </li>
          <li>
            <span className="font-medium">Communicate with you</span> (including responding to your requests, inquiries,
            comments, and suggestions);
          </li>
          <li>
            <span className="font-medium">Personalize content</span> (including tailoring content we send or display to
            you, offering location customization, and personalizing your experiences while using our Services);
          </li>
          <li>
            <span className="font-medium">Further our networking, marketing, and social strategies</span> (including
            notifying you about services, offers, promotions, and events we think might be of interest to you and using
            information that we learn about you to assist us in advertising our Services on third-party websites);
          </li>
          <li>
            <span className="font-medium">
              Protect against, identify, investigate, and respond to misuse of our Services
            </span>{" "}
            (including preventing and detecting fraud, unauthorized access, and other unlawful behavior);
          </li>
          <li>
            <span className="font-medium">Support our general business operations</span> (including maintaining our
            business and legal recordkeeping); and
          </li>
          <li>
            <span className="font-medium">
              Carry out any other purpose described to you at the time the information was collected
            </span>
            .
          </li>
        </ul>
      </section>

      <section className="mt-8 space-y-4" id="how-we-share">
        <FigmaTypography component="h2" variant="memo-heading-1">
          How We Share Your Information
        </FigmaTypography>
        <p className="leading-relaxed">
          We may share information about you with certain third parties in the following circumstances:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <span className="font-medium">Companies under common control:</span> We may share data between and among any
            current or future parents, subsidiaries, affiliates, and other companies under common control and ownership
            with Innovera.
          </li>
          <li>
            <span className="font-medium">Vendors and service providers:</span> We may share Personal Information with
            vendors, consultants, and other service providers who need to access the data to perform services on our
            behalf, such as providing analytics services, cloud-hosting, or email marketing services. In such case, we
            ensure through contractual measures that your Personal Information is processed and protected in compliance
            with applicable laws and this Privacy Notice.
          </li>
          <li>
            <span className="font-medium">Analytics partners:</span> We may share your Personal Information with other
            third parties that provide online analytics services, which help us better understand how individuals
            interact with our Services and email communications.
          </li>
          <li>
            <span className="font-medium">Business transfers:</span> We may share Personal Information with another
            company in connection with or during negotiations of any merger, acquisition, financing, reorganization,
            bankruptcy, sale of all or a portion of our assets, or transition of services to another provider.
          </li>
          <li>
            <span className="font-medium">Legal requirements:</span> We may share Personal Information when we believe
            it is necessary to comply with a legal obligation, including lawful requests from public authorities to meet
            national security or law enforcement requirements. We may also share Personal Information when we believe it
            is necessary to protect Innovera's rights and property, to protect the safety of our users, and to defend
            against legal liability.
          </li>
          <li>
            <span className="font-medium">Consent:</span> We may share Personal Information with third parties with your
            consent and at your direction, including if we notify you that the information you provide will be shared in
            a particular manner and you provide that information.
          </li>
          <li>
            <span className="font-medium">Social media platforms:</span> We may offer social sharing features and other
            integrated tools such as widgets, which allow you to share content on our Site with third-party social media
            platforms like Facebook or LinkedIn. Using these social sharing features is optional. You should always
            review, and if necessary, adjust your privacy settings on third-party social media platforms before linking
            or connecting them to our Site or Services.
          </li>
        </ul>
        <p className="leading-relaxed">
          We may also share aggregate or de-identified information, which cannot reasonably be used to identify you, for
          various purposes including compliance with various reporting obligations; for business or marketing purposes;
          or to assist third parties in understanding our users' interests, habits, and usage patterns for certain
          programs, content, services, advertisements, promotions, and/or functionality available through the Services.
        </p>
      </section>

      <section className="mt-8 space-y-4" id="your-rights">
        <FigmaTypography component="h2" variant="memo-heading-1">
          Your Rights and Choices
        </FigmaTypography>
        <p className="leading-relaxed">
          You have a variety of rights and choices related to our use of your Personal Information.
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <span className="font-medium">Opting out of promotional messages.</span> You can opt out of receiving our
            promotional emails at any time. You may do so by contacting our customer service team or by following the
            opt-out instructions in the promotional emails or texts we send you. Please be aware that it may take up to
            ten (10) days for us to process your request, and you may continue receiving promotional communications from
            us during that period. If you opt out of receiving such communications, please be aware that we may continue
            to send you non-promotional emails (such as emails related to our business relationship or emails about
            changes to our legal terms).
          </li>
          <li>
            <span className="font-medium">Restricting cookies/Do Not Track.</span> Innovera does not currently change
            its behavior in response to web browser "do not track" signals. However, you can configure most browsers to
            reject cookies or to notify you when you are sent a cookie, giving you a chance to decide whether to accept
            it. You can consult the help section of your browser to find out how to do this. Please note that if you
            choose to remove or reject cookies, this could affect the availability and functionality of our Services. In
            the event that a universally accepted standard emerges on how organizations should respond to "do not track"
            signals, we will assess and provide an appropriate response to those signals.
          </li>
          <li>
            <span className="font-medium">Accessing, updating, or deleting your personal information.</span> If you
            would like to access, update, or delete Personal Information you have provided you may do so through your
            account settings or by emailing us at{" "}
            <a className="text-blue-500 underline" href="mailto:compliance@innovera.ai">
              compliance@innovera.ai
            </a>
            . Subject to your agreement with us, you may close your account by emailing us at{" "}
            <a className="text-blue-500 underline" href="mailto:compliance@innovera.ai">
              compliance@innovera.ai
            </a>
            . Please note that we may keep certain Personal Information as necessary to comply with our legal
            obligations or for legitimate business purposes, such as to resolve disputes or enforce our agreements. We
            may also keep archived copies of Personal Information for a certain period. In the event that Innovera
            cannot delete your Personal Information, we will inform you of the reasons why, subject to any legal or
            regulatory restrictions.
          </li>
        </ul>
        <p className="leading-relaxed">
          <span className="font-medium">A note about accuracy:</span> Certain features of our Services rely on AI
          technology to parse information and generate output. Given the probabilistic nature of AI models, sometimes
          the output produced is not 100% accurate or up-to-date. If you notice that search results or other output
          within our Services contains inaccurate or outdated information about you, you may ask us to correct the
          inaccuracy or remove the information by emailing us at{" "}
          <a className="text-blue-500 underline" href="mailto:compliance@innovera.ai">
            compliance@innovera.ai
          </a>
          . Given the complexity of how AI models work, we may not be able to correct the inaccuracy or delete the
          information in every instance.
        </p>
      </section>

      <section className="mt-8 space-y-4" id="eea-uk-ch">
        <FigmaTypography component="h2" variant="memo-heading-1">
          Additional Disclosures for Residents of the EEA, UK, and Switzerland
        </FigmaTypography>
        <p className="leading-relaxed">
          The laws in the European Economic Area ("<strong>EEA</strong>"), United Kingdom ("<strong>UK</strong>"), and
          Switzerland require businesses to disclose certain information, including (i) the controller(s) of your
          Personal Information, (ii) the legal bases relied on to use or disclose your Personal Information (iii) your
          legal rights with respect to your Personal Information, (iv) safeguards used for transferring your Personal
          Information outside of these jurisdictions, and (v) the contact details of the controller and/or data
          protection offer. To the extent those laws apply, our answers are as follows:
        </p>
        <div className="space-y-3">
          <div>
            <p className="font-medium">Controller</p>
            <p className="leading-relaxed">
              Innovera is considered the "data controller" of the Personal Information that we manage under this Privacy
              Notice.
            </p>
          </div>
          <div>
            <p className="font-medium">Legal Basis</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                <span className="font-medium underline">Legitimate Interest.</span> In most cases, we manage Personal
                Information on the ground that it furthers our legitimate interests in commercial activities such as the
                following in ways that are not overridden by the interests or fundamental rights and freedoms of the
                affected individuals:
                <ul className="list-[circle] pl-6 space-y-1">
                  <li>Providing Professional Services pursuant to our contract with our customers;</li>
                  <li>Processing billing and payments;</li>
                  <li>Marketing our Services;</li>
                  <li>Protecting our customers, personnel, and property; and</li>
                  <li>Analyzing and improving our business.</li>
                </ul>
              </li>
              <li>
                <span className="font-medium underline">Legal Compliance.</span> We need to use and disclose Personal
                Information in certain ways to comply with our legal obligations, such as complying with trade control
                laws and legal and business recordkeeping requirements.
              </li>
              <li>
                <span className="font-medium underline">To Honor Our Contractual Commitments to an Individual.</span> We
                need to process some Personal Information to meet our contractual obligations to the individual to whom
                the Personal Information pertains.
              </li>
              <li>
                <span className="font-medium underline">Consent.</span> Where required by law, and in some other cases,
                we manage Personal Information based on implied or express consent. For instance, we will obtain and
                rely on your consent to send you newsletters or for the use of certain cookies.
              </li>
            </ul>
          </div>
          <div>
            <p className="font-medium">Legal Rights</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                <span className="font-medium underline">Right to Access & Portability:</span> You have the right to
                request that we provide access to and/or a copy of the Personal Information we hold about you
                (including, in some cases, in an easily accessible format along with information explaining how that
                data is used). You may also request that we transfer your Personal Information to a third party.
              </li>
              <li>
                <span className="font-medium underline">Right to Correction:</span> You have the right to request that
                we update Personal Information about you, which is out-of-date or incorrect.
              </li>
              <li>
                <span className="font-medium underline">Right to Restrict Processing:</span> In certain cases where we
                process your information, you may also have the right to restrict or limit the ways in which we use your
                Personal Information.
              </li>
              <li>
                <span className="font-medium underline">Right to Deletion:</span> In certain circumstances, you have the
                right to request the deletion of your Personal Information, except information we are required to retain
                by law, regulation, or to protect the safety, security, and integrity of our business.
              </li>
              <li>
                <span className="font-medium underline">Right to Object:</span> If we process your Personal Information
                based on our legitimate interests as explained above, or in public interest, you can object to this
                processing in certain circumstances. In such cases, we will stop processing your Personal Information
                unless we have a compelling legitimate ground to continue processing or where it is legally necessary.
              </li>
              <li>
                <span className="font-medium underline">Right to Withdraw Consent:</span> Where we rely on consent, you
                can withdraw your consent to our processing by using various means we offer, such as an email
                unsubscribe link. This is without prejudice to your right to permanently delete your Personal
                Information.
              </li>
              <li>
                We will consider all requests and provide our response within the time stated by applicable law. Please
                be aware that certain information may be exempt from such requests in some circumstances. Before
                responding to your request, we may require that you provide us with additional information to confirm
                your identity.
              </li>
              <li>
                You have the right to lodge a complaint with the appropriate supervisory authority if you believe our
                processing of your Personal Information violates applicable law.
              </li>
            </ul>
          </div>
          <div>
            <p className="font-medium">Safeguards</p>
            <p className="leading-relaxed">
              Personal Information we collect may be stored and processed for the purposes set out in this Privacy
              Notice in the United States where we are based or in any country where we have operations or engage
              vendors or service providers. These countries may have different data protection and government
              surveillance laws than your country.
            </p>
            <p className="leading-relaxed mt-2">
              Some of these countries have received a European Commission adequacy decision. For other countries, we
              take organizational, contractual, and legal measures, such as entering into written agreements with the
              relevant third party, to ensure your Personal Information is adequately protected and processed
              exclusively for the purposes set forth above. In most cases, our legal reason for processing and
              transferring your Personal Information is to fulfill a contract with you or to provide the services or
              assistance you have requested. For additional information regarding these transfers, please contact us at{" "}
              <a className="text-blue-500 underline" href="mailto:compliance@innovera.ai">
                compliance@innovera.ai
              </a>
              .
            </p>
          </div>
          <div>
            <p className="font-medium">Electronic Direct Marketing</p>
            <p className="leading-relaxed">
              Innovera may send you electronic direct marketing subject to national law, if you have not declined such
              messages. If you do not wish to receive such messages any longer, you can opt-out at any time by following
              the instructions in those messages or by contacting us at{" "}
              <a className="text-blue-500 underline" href="mailto:compliance@innovera.ai">
                compliance@innovera.ai
              </a>
              .
            </p>
          </div>
          <div>
            <p className="font-medium">Contact Information for Compliancy Privacy Officer</p>
            <address className="not-italic leading-relaxed">
              For personal information we process under this Privacy Notice:
              <br />
              309 Quinnhill Avenue, Los Altos, CA 94024
            </address>
          </div>
        </div>
      </section>

      <section className="mt-8 space-y-4" id="childrens-privacy">
        <FigmaTypography component="h2" variant="memo-heading-1">
          Children's Privacy
        </FigmaTypography>
        <p className="leading-relaxed">
          Our Services are intended for adults, and we do not knowingly collect Personal Information from children. If
          you are a parent or legal guardian and think your child is under 13 (or a higher age threshold where
          applicable) has given us Personal Information without your consent, please contact us at{" "}
          <a className="text-blue-500 underline" href="mailto:compliance@innovera.ai">
            compliance@innovera.ai
          </a>
          .
        </p>
      </section>

      <section className="mt-8 space-y-4" id="third-party-links">
        <FigmaTypography component="h2" variant="memo-heading-1">
          Links to Other Websites and Third-Party Content
        </FigmaTypography>
        <p className="leading-relaxed">
          We may provide links to third-party websites, services, and applications that are not operated on or
          controlled by Innovera. This Privacy Notice does not apply to the privacy practices of those third parties.
          The fact that we link to a website, service, or application is not an endorsement, authorization, or
          representation of our affiliation with that third party. We encourage you to review the privacy policies of
          any third-party service before providing any Personal Information to or through them.
        </p>
      </section>

      <section className="mt-8 space-y-4" id="data-retention">
        <FigmaTypography component="h2" variant="memo-heading-1">
          Data Retention
        </FigmaTypography>
        <p className="leading-relaxed">
          We only keep your Personal Information for so long as reasonably necessary for the purposes described in this
          Privacy Notice, as required by law, or as necessary to resolve disputes and enforce our rights and agreements.
          All outdated, unused, and obsolete data is deleted or anonymized. Data may persist in copies made for backup
          and business continuity purposes for additional time.
        </p>
      </section>

      <section className="mt-8 space-y-4" id="security">
        <FigmaTypography component="h2" variant="memo-heading-1">
          Security
        </FigmaTypography>
        <p className="leading-relaxed">
          Innovera uses accepted administrative, physical, and technical safeguards we believe are appropriate to
          protect the confidentiality, integrity, and availability of your Personal Information. Although we make
          reasonable efforts to protect Personal Information from loss, misuse, or alteration by third parties, you
          should be aware that there is always some risk involved in transmitting information over the Internet and
          storing information electronically. Innovera cannot and does not guarantee absolute security.
        </p>
      </section>

      <section className="mt-8 space-y-4" id="changes">
        <FigmaTypography component="h2" variant="memo-heading-1">
          Changes to Our Privacy Notice
        </FigmaTypography>
        <p className="leading-relaxed">
          We may change this Privacy Notice from time to time to reflect changes in our practices or in the law. If we
          update this notice, we will post the updated Privacy Notice on our website and indicate when it was last
          revised. You are advised to review this notice periodically to stay informed of our practices. If we make
          material changes, we may provide you with additional notice, such as posting a statement on our homepage or
          sending you an email notification, if we have your email address on file. Your continued use of the Service
          after the revised Privacy Notice has become effective indicates that you have read, understood, and agreed to
          the current version of this Privacy Notice, to the extent permitted by law.
        </p>
        <p className="leading-relaxed">
          Updated Privacy Notice is always available on our website for your convenience.
        </p>
      </section>

      <section className="mt-8 space-y-4" id="contact">
        <FigmaTypography component="h2" variant="memo-heading-1">
          Contacting Us
        </FigmaTypography>
        <p className="leading-relaxed">
          If you have any questions about our Privacy Notice or how we protect your Personal Information please contact
          us at{" "}
          <a className="text-blue-500 underline" href="mailto:compliance@innovera.ai">
            compliance@innovera.ai
          </a>{" "}
          or you may write us at:
        </p>
        <address className="not-italic leading-relaxed">309 Quinnhill Avenue, Los Altos, CA 94024</address>
      </section>
    </article>
  );
}
